import React, { useEffect, useMemo, useState } from 'react';
import { Result, Progress, Space, Image, message } from 'antd';
import { useDispatch } from 'react-redux';
import { isAppModule, DASHBOARD_TYPE } from '@utils/constants';
import { createTempDashboard } from '@store/tempDashboard/actions';
import { removeBackgroundTab } from '@store/dashboards/tabs/actions';
import { removeDashboard } from '@store/dashboards/remove/actions';
import { FooterWrapper, ProgressWrapper, Slide, StyledCarousel } from './styled';
import { setLandingPage } from '@api/landingPage';
import { useDefaultLandingPageStore } from '@globalStore/defaultLandingPageStore';
import apptracker from '@utils/sensor/apptracker';
import { createSavedDashboardConfig, createTempDashboardConfig } from './migrationUtils';
import useSaveDashboard from '@components/saving/useSaveDashboard';
import { parseDashboardURL } from '@utils';
import { UpCircleOutlined } from '@ant-design/icons';

import IMG1 from '@images/layout/comp-1.png';
import IMG2 from '@images/layout/comp-2.png';
import { COMPARE_MIGRATION_ERROR, MIGRATED_COMPARE_DASHBOARD } from '@src/utils/sensor/track';
import { deleteSharedTab } from '@store/sharedTabs';
import { useNavigate } from 'react-router-dom';

enum CHOICE {
  new = 'new',
  legacy = 'legacy',
}

function NewCompareDashboard({ tabData, fallback }) {
  const dispatch = useDispatch();
  const { saveMutation } = useSaveDashboard();
  const [loadingPercent, setLoadingPercent] = useState(0);
  const navigate = useNavigate();

  // loading effects
  const increaseLoadingPercent = () => {
    setLoadingPercent((prevPercent) => {
      const newPercent = prevPercent + Math.floor(Math.random() * (25 - 10) + 10);
      if (newPercent > 90) {
        return 90;
      }
      return newPercent;
    });
  };

  const handleMigration = async () => {
    const {
      config = null,
      name = 'compare dashboard',
      isSaved = false,
      isSharing = false,
      id,
    } = tabData;

    try {
      if (config) {
        if (isSaved) {
          // create new saved dashboard
          const savePayload = createSavedDashboardConfig({ config, name });
          const { data } = await saveMutation.mutateAsync(savePayload);

          const tab = {
            id: data.id,
            dashboardTypeId: savePayload.dashboardTypeId,
          };

          // remove old one from db
          dispatch(
            removeDashboard(id, function* () {
              yield null;
            }),
          );

          // redirect to new dashboard
          setTimeout(() => {
            navigate(parseDashboardURL(tab));
            // dispatch(createSavedDashboard(data.id, data.dashboard, savePayload.dashboardTypeId, savePayload.config));
          }, 500);
        } else {
          // create temp dashboard for temp and shared compare dashboards
          const tempConfig = createTempDashboardConfig(config);

          setTimeout(() => {
            dispatch(
              createTempDashboard(
                isAppModule
                  ? DASHBOARD_TYPE.APP_EXPERIENCE_DRILLDOWN
                  : DASHBOARD_TYPE.TRENDS_DRILLDOWN,
                tempConfig,
                `${name}`,
              ),
            );
          }, 500);
        }

        // clear pulse 5 tab and localStorage
        setTimeout(() => {
          if (isSharing) {
            dispatch(deleteSharedTab(id, false));
          } else {
            dispatch(removeBackgroundTab(id));
          }
        }, 600);

        apptracker(MIGRATED_COMPARE_DASHBOARD, { isSaved, id });
        setLoadingPercent(100);
      }
    } catch (err) {
      apptracker(COMPARE_MIGRATION_ERROR, { isSaved, id, err });
      fallback();
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      increaseLoadingPercent();
    }, 500);
    return () => clearInterval(interval);
  }, []);

  // start migration process on mount
  useEffect(() => {
    handleMigration();
  }, []);

  return (
    <ProgressWrapper>
      Generating new dashboard...
      <Progress percent={loadingPercent} />
    </ProgressWrapper>
  );
}

function Migrate({ tabData, isSharing, id }) {
  // Use Zustand store instead of Redux selector
  const defaultLandingPage = useDefaultLandingPageStore((state) => ({
    url: state.url,
    path: state.path,
    name: state.name,
  }));
  const updateDefaultLandingPage = useDefaultLandingPageStore(
    (state) => state.updateDefaultLandingPage,
  );
  const [choice, setChoice] = useState(CHOICE.new);

  const isDefaultLandingPage = useMemo(() => {
    const { pathname } = window.location;
    const defaultPathname = defaultLandingPage.url || '';
    return defaultPathname.endsWith(pathname);
  }, [defaultLandingPage.url]);

  // if the legacy dashboard was set as the default dashboard
  // reset
  useEffect(() => {
    if (isDefaultLandingPage) {
      const payload = { url: '', name: '', path: '' };
      setLandingPage(payload).then(() => {
        updateDefaultLandingPage(payload);
      });
    }
  }, [isDefaultLandingPage]);

  const resetChoice = () => setChoice(CHOICE.new);

  const fallback = () => {
    message.error('There was an error while generating new compare dashboard.');
    resetChoice();
  };

  if (!tabData) {
    return <div>loading....</div>;
  }

  return (
    <div>
      <Result
        status="info"
        icon={<UpCircleOutlined />}
        title="Upgrading this dashboard to New Compare..."
        subTitle={
          <Space direction="vertical">
            <div>
              New Compare is based on Trends with instant drill-down capability and all the features
              of Legacy Compare including support for upto 20 filters.
            </div>
            <div>Legacy Compare will be deprecated very soon.</div>
          </Space>
        }
        extra={[
          choice ? (
            <NewCompareDashboard tabData={tabData} fallback={fallback} />
          ) : (
            <div>
              <StyledCarousel dotPosition="top" autoplay>
                <div key="1">
                  <Slide>
                    <Image src={IMG1} />
                  </Slide>
                </div>
                <div key="2">
                  <Slide>
                    <Image src={IMG2} />
                  </Slide>
                </div>
              </StyledCarousel>
              <FooterWrapper></FooterWrapper>
            </div>
          ),
        ]}
      />
    </div>
  );
}

export default Migrate;
